import { useCallback, useEffect, useRef, useState } from "react";

const getSR = () => window.SpeechRecognition || window.webkitSpeechRecognition;
export const speechSupported = () => !!getSR();
const IS_ANDROID = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

const WAKE_RE = /\b(hey|hi|hey there|ok|okay)\s+(apex|apox|apax|epex)\b/i;
const STOP_RE = /\b(stop|wait|hang on|hold on|shut up|quiet|enough|no no)\b/i;

const wordSet = (text) =>
  new Set(String(text || "").toLowerCase().split(/[^a-z']+/).filter((w) => w.length >= 3));

const toWords = (t) => (t || "").trim().split(/\s+/).filter(Boolean);
const norm = (w) => w.toLowerCase().replace(/[^a-z0-9]/g, "");

// Longest common subsequence length of two word arrays (small inputs only).
function lcs(a, b) {
  const dp = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    let prev = 0;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev + 1 : Math.max(dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[b.length];
}

// Words of `next` that aren't already said at the end of `prev`. Tolerates small recogniser
// revisions ("let's"/"lets", an inserted "The") so cumulative / replayed results never stack up.
export function stripOverlap(prev, next) {
  const a = toWords(prev).map(norm).filter(Boolean);
  const bRaw = toWords(next);
  const b = bRaw.map(norm);
  if (!b.length) return "";
  if (!a.length) return bRaw.join(" ");
  const tail = a.slice(-Math.max(b.length + 2, 3));
  // next is (almost) entirely a replay of what we already have
  if (b.length >= 2 && lcs(tail, b) >= Math.ceil(b.length * 0.8)) return "";
  // suffix of prev ≈ prefix of next → keep only the new words
  for (let k = Math.min(a.length, b.length); k >= 2; k--) {
    if (lcs(a.slice(-k), b.slice(0, k)) >= Math.ceil(k * 0.8)) return bRaw.slice(k).join(" ");
  }
  return bRaw.join(" ");
}

export function mergeSpeech(prev, next) {
  const a = toWords(prev).map(norm).filter(Boolean);
  const b = toWords(next).map(norm);
  if (!a.length) return (next || "").trim();
  if (!b.length) return (prev || "").trim();
  // next is a revised, cumulative version of prev (Android) → replace
  if (b.length >= a.length && lcs(a, b.slice(0, a.length + 2)) >= Math.ceil(a.length * 0.75)) return (next || "").trim();
  return [(prev || "").trim(), stripOverlap(prev, next)].filter(Boolean).join(" ");
}

// Web Speech API layer used for wake-word detection, live transcript and barge-in.
// mode: "off" | "wake" | "live"
//  - "wake": listens for "Hey Apex" and fires onWake(rest)
//  - "live": streams interim words (exposed as `interim`) and fires onFinal(text) once per utterance.
// Barge-in: while `speaking` is true, incoming audio is treated as TTS echo UNLESS it contains
// words Apex isn't saying (or a stop command) — then onBargeIn() fires and capture restarts clean.
// Results already sent or discarded as echo are skipped via a per-session start index, so they
// can never be rebuilt into a later message.
export function useSpeechRecognition({ mode, speaking = false, spokenText = "", onWake, onFinal, onBargeIn, onError }) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const recRef = useRef(null);
  const modeRef = useRef(mode);
  const stoppingRef = useRef(false);
  const committedRef = useRef("");  // text carried over from earlier recogniser sessions
  const bufRef = useRef("");        // committed + this session's finals (not yet sent)
  const silenceTimerRef = useRef(null);
  const wakeTimerRef = useRef(null);
  const baseIdxRef = useRef(0);     // ignore e.results before this index (already sent / echo)
  const lastLenRef = useRef(0);     // e.results.length at the latest event
  const speakingRef = useRef(false);
  const spokenWordsRef = useRef(new Set());
  const bargedRef = useRef(false);
  const cbRef = useRef({ onWake, onFinal, onBargeIn, onError });
  useEffect(() => { cbRef.current = { onWake, onFinal, onBargeIn, onError }; });
  useEffect(() => { modeRef.current = mode; });

  const clearSilence = () => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    clearTimeout(wakeTimerRef.current);
  };

  // Forget everything heard so far in this session (sent, or echo).
  const dropHeard = () => {
    committedRef.current = "";
    bufRef.current = "";
    baseIdxRef.current = lastLenRef.current;
  };

  useEffect(() => {
    const was = speakingRef.current;
    speakingRef.current = !!speaking;
    spokenWordsRef.current = wordSet(spokenText);
    if (speaking && !was) {
      bargedRef.current = false;
    } else if (!speaking && was) {
      if (!bargedRef.current) { dropHeard(); setInterim(""); clearSilence(); }
      bargedRef.current = false;
    }
  }, [speaking, spokenText]);

  const commit = useCallback(() => {
    clearSilence();
    const text = bufRef.current.trim();
    dropHeard();
    setInterim("");
    if (text) cbRef.current.onFinal && cbRef.current.onFinal(text);
  }, []);

  const stop = useCallback(() => {
    stoppingRef.current = true;
    clearSilence();
    committedRef.current = "";
    bufRef.current = "";
    const rec = recRef.current;
    recRef.current = null;
    if (rec) {
      try { rec.onend = null; rec.onresult = null; rec.onerror = null; rec.stop(); } catch (e) {}
    }
    setListening(false);
    setInterim("");
  }, []);

  const start = useCallback((m) => {
    const SR = getSR();
    if (!SR) { cbRef.current.onError && cbRef.current.onError(new Error("unsupported")); return; }
    stop();
    stoppingRef.current = false;
    baseIdxRef.current = 0;
    lastLenRef.current = 0;

    const rec = new SR();
    // Android Chrome's continuous mode returns cumulative, duplicated results — use one utterance per session there.
    rec.continuous = !IS_ANDROID;
    rec.interimResults = true;
    rec.lang = "en-AU";
    recRef.current = rec;

    rec.onstart = () => setListening(true);

    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        cbRef.current.onError && cbRef.current.onError(e);
        stop();
      }
    };

    rec.onend = () => {
      setListening(false);
      if (stoppingRef.current || modeRef.current !== m || recRef.current !== rec) return;
      // New session: carry unsent text forward, results restart at index 0.
      committedRef.current = bufRef.current;
      baseIdxRef.current = 0;
      lastLenRef.current = 0;
      try { rec.start(); } catch (e) {}
    };

    rec.onresult = (e) => {
      lastLenRef.current = e.results.length;
      let sessionFinal = "";
      let interimText = "";
      for (let i = baseIdxRef.current; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) sessionFinal = mergeSpeech(sessionFinal, r[0].transcript);
        else interimText = mergeSpeech(interimText, r[0].transcript);
      }
      interimText = stripOverlap(sessionFinal, interimText);

      if (m === "wake") {
        const hay = mergeSpeech(mergeSpeech(committedRef.current, sessionFinal), interimText);
        const hit = hay.match(WAKE_RE);
        if (!hit) return;
        // Wait for a short pause so "Hey Apex, brief me" arrives as one command.
        const rest = hay.slice(hit.index + hit[0].length).replace(/^[\s,.!?]+/, "").trim();
        clearTimeout(wakeTimerRef.current);
        wakeTimerRef.current = setTimeout(() => {
          dropHeard();
          cbRef.current.onWake && cbRef.current.onWake(rest);
        }, 900);
        return;
      }

      const nextBuf = mergeSpeech(committedRef.current, sessionFinal);
      const full = mergeSpeech(nextBuf, interimText);

      // While Apex is speaking, decide barge-in vs echo. Never commit mid-speech.
      if (speakingRef.current && !bargedRef.current) {
        const novel = full.toLowerCase().split(/[^a-z']+/)
          .filter((w) => w.length >= 3 && !spokenWordsRef.current.has(w));
        if (STOP_RE.test(full) || novel.length >= 2) {
          bargedRef.current = true;
          cbRef.current.onBargeIn && cbRef.current.onBargeIn();
          dropHeard(); setInterim(""); clearSilence();
        }
        return;
      }

      bufRef.current = nextBuf;
      setInterim(full);
      clearSilence();
      if (full) silenceTimerRef.current = setTimeout(() => {
        bufRef.current = full;  // include any words Android never marked final
        commit();
      }, 1300);
    };

    try { rec.start(); } catch (e) {}
  }, [stop, commit]);

  useEffect(() => {
    if (mode === "off") { stop(); return; }
    start(mode);
    return () => stop();
  }, [mode, start, stop]);

  return { listening, interim };
}
